import { Track } from "@/src/lib/tracking";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMemoizedFn } from "ahooks";
import SwapSkeleton from "./SwapSkeleton";
import { useIsPc } from "@hooks/useIsPc";

interface IframeMessage {
  type: "iframe-height-change" | "iframe-swap-close";
  width?: number;
  height?: number;
}

interface SwapContainerProps {
  url: string;
  onExit?: () => void;
}

const SwapContainer: React.FC<SwapContainerProps> = ({ url, onExit }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isPc] = useIsPc();
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const pcWidth = useMemo(() => {
    const width = document.documentElement.clientWidth * 0.32;
    return width < 480 ? width : 480;
  }, []);
  const [iframeSize, setIframeSize] = useState(
    isPc
      ? {
          width: `${pcWidth}px`,
          height: `${pcWidth * 1.125}px`,
        }
      : {
          width: "100%",
          height: "calc(100vh - 100px)",
        },
  );

  const updateIframeHeight = useMemoizedFn((height?: number) => {
    if (!isPc || !height) {
      return;
    }
    setIframeSize({
      width: `${pcWidth}px`,
      height: `${Math.min(height, document.documentElement.clientHeight * 0.9)}px`,
    });
  });

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        ![
          "https://swap.edwealth.ai",
          "https://swapapp-beta.vercel.app",
          "http://localhost:3000",
        ].includes(event.origin)
      ) {
        return;
      }

      try {
        const data = event.data as IframeMessage;
        switch (data.type) {
          case "iframe-height-change":
            updateIframeHeight(data.height);

            // 处理大小调整事件
            // if (data.width && data.height) {
            //   setIframeSize({
            //     width: `${data.width}px`,
            //     height: `${data.height}px`,
            //   });
            // }
            break;

          case "iframe-swap-close":
            // 处理退出事件
            if (onExit) {
              onExit();
            }
            break;

          default:
        }
      } catch (error) {
        Track.log("error", "SwapContainer", "处理iframe消息时出错:", {
          cause: String(error),
        });
      }
    };

    // 添加消息事件监听器
    window.addEventListener("message", handleMessage);

    // 清理函数
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [onExit]);

  useEffect(() => {
    setIframeSize(
      isPc
        ? {
            width: `${pcWidth}px`,
            height: `${pcWidth * 1.125}px`,
          }
        : {
            width: "100%",
            height: "calc(100vh - 100px)",
          },
    );
  }, [isPc]);

  return (
    <div className="swap-container relative">
      {!isIframeLoaded && (
        <SwapSkeleton
          iframeWidth={isPc ? pcWidth : document.documentElement.clientWidth}
          onExitClick={onExit}
          style={{
            width: iframeSize.width,
            height: iframeSize.height,
            zIndex: 10,
            position: "absolute",
          }}
        />
      )}
      <iframe
        ref={iframeRef}
        src={url}
        style={{
          width: iframeSize.width,
          height: iframeSize.height,
          border: "none",
          borderRadius: "16px",
          zIndex: 1,
        }}
        onLoad={() => {
          setIsIframeLoaded(true);
        }}
      />
    </div>
  );
};

export default SwapContainer;
